import type { MeasurementUnit } from '@/domains/measurement-units/types/models/MeasurementUnit.types'
import type {
  MeasurementTypesIndexPayload,
  MeasurementUnitsDestroyPayload,
  MeasurementUnitsIndexPayload,
  MeasurementUnitsStorePayload,
  MeasurementUnitsUpdatePayload
} from '@/domains/measurement-units/types/requests/EndpointRequest.types'
import type {
  MeasurementTypesIndexResponse,
  MeasurementUnitsIndexResponse
} from '@/domains/measurement-units/types/responses/MeasurementUnitResponse.types'

import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil'

/**
 * API collection for measurement units.
 *
 * Backend: App\Admin\Http\Domains\Measurement\Controllers\MeasurementUnitController
 * and MeasurementUnitTypeController. Reads are gated by `measurement_units.view`,
 * writes by `measurement_units.administer`. A rule refusal returns 422 with a
 * plain `message` and no field key.
 */
const callMeasurementUnits = () => {
  /** Fetch the flat unit list (pickers). The endpoint does not paginate — it serves every unit. */
  const index = (payload: MeasurementUnitsIndexPayload = {}) => {
    return twineServiceClient.get<MeasurementUnitsIndexResponse>({
      endpoint: relativeZiggyRoute('admin.measurements.units.index'),
      queryParams: payload.queryParams,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /** Fetch every type with its units nested (the grouped management screen). */
  const types = (payload: MeasurementTypesIndexPayload = {}) => {
    return twineServiceClient.get<MeasurementTypesIndexResponse>({
      endpoint: relativeZiggyRoute('admin.measurements.types.index'),
      token: payload.token
    })
  }

  const store = (payload: MeasurementUnitsStorePayload) => {
    return twineServiceClient.post<MeasurementUnit>({
      endpoint: relativeZiggyRoute('admin.measurements.units.store'),
      body: payload.body,
      token: payload.token
    })
  }

  const update = (payload: MeasurementUnitsUpdatePayload) => {
    return twineServiceClient.put<MeasurementUnit>({
      endpoint: relativeZiggyRoute('admin.measurements.units.update', { measurementUnit: payload.id }),
      body: payload.body,
      token: payload.token
    })
  }

  const destroy = (payload: MeasurementUnitsDestroyPayload) => {
    return twineServiceClient.delete<void>({
      endpoint: relativeZiggyRoute('admin.measurements.units.destroy', { measurementUnit: payload.id }),
      token: payload.token
    })
  }

  return {
    index,
    types,
    store,
    update,
    destroy
  }
}

export { callMeasurementUnits }
